import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LuChevronLeft, LuChevronRight, LuMaximize, LuX, LuSend, LuCheck } from "react-icons/lu";
import axios from "../../axios";
import { fetchAuthMe, setStars } from "../../redux/slices/auth";
import StarIcon from "../../components/ui/StarIcon";
import SlideFrame from "./SlideFrame";
import { AnswersContext } from "./answers";
import * as styled from "./PresentPage.styled";
import * as hw from "./HomeworkPage.styled";

const STAGE_W = 1280;
const STAGE_H = 720;

// Інтерактивне ДЗ: учень виконує слайди розділу Homework і надсилає; вчитель відкриває те саме з відповідями учня (лише перегляд).
// Відповідей-підказок (кнопка Answers, нотатки) тут немає взагалі.
function HomeworkPage() {
  const { markedLessonId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: me } = useSelector((state) => state.auth);
  const isStudent = me?.userType === "student";

  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [results, setResults] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const viewportRef = useRef(null);
  const valuesRef = useRef({});

  useEffect(() => {
    if (!me || typeof me !== "object" || !me.userType) dispatch(fetchAuthMe());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    axios
      .get(`homework-player/${markedLessonId}`)
      .then((res) => {
        // Попередні відповіді (повторний вхід або перегляд учителем) — у сховище до першого рендера слайдів
        valuesRef.current = JSON.parse(JSON.stringify(res.data.marked.answers || {}));
        setResults(res.data.marked.results || {});
        setPayload(res.data);
      })
      .catch(() => setError("ДЗ не знайдено або немає доступу"));
  }, [markedLessonId]);

  // Учень відповідає; вчитель/адмін — лише дивиться. Вирішує сервер (canSubmit), а не збережений у браузері вхід;
  // поки відповіді немає — теж лише перегляд
  const readOnly = payload?.canSubmit !== undefined ? !payload.canSubmit : !(Boolean(me?.userType) && isStudent);

  const store = useMemo(
    () => ({
      values: valuesRef.current,
      save: (slideId, key, value) => {
        valuesRef.current[slideId] = { ...(valuesRef.current[slideId] || {}), [key]: value };
      },
      report: (slideId, result) =>
        setResults((prev) => {
          const old = prev[slideId];
          if (old && old.correct === result.correct && old.total === result.total && old.answered === result.answered) return prev;
          return { ...prev, [slideId]: result };
        }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payload]
  );

  useLayoutEffect(() => {
    const fit = () => {
      const el = viewportRef.current;
      if (!el) return;
      setScale(Math.min(el.clientWidth / STAGE_W, el.clientHeight / STAGE_H));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [payload]);

  const slides = payload?.slides || [];
  const total = slides.length;
  const go = useCallback((next) => setIndex((current) => Math.max(0, Math.min(total - 1, typeof next === "function" ? next(current) : next))), [total]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.target.closest("input, textarea")) return;
      if (event.key === "ArrowRight") go((i) => i + 1);
      else if (event.key === "ArrowLeft") go((i) => i - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  const isDone = (slide) => (results[slide.id]?.answered || 0) > 0;

  const send = async () => {
    setConfirm(null);
    setSending(true);
    try {
      const { data } = await axios.post(`homework-player/${markedLessonId}/submit`, { answers: valuesRef.current, results });
      if (data.starAwarded) dispatch(setStars(data.stars));
      setPayload((prev) => ({ ...prev, marked: { ...prev.marked, submittedAt: new Date().toISOString(), score: data.score } }));
      setDone(data);
    } catch (e) {
      setError("Не вдалося надіслати ДЗ. Спробуй ще раз.");
    } finally {
      setSending(false);
    }
  };

  const trySend = () => {
    const missing = slides.map((slide, i) => (isDone(slide) ? null : i + 1)).filter(Boolean);
    if (missing.length) setConfirm(missing);
    else send();
  };

  // Відкрито в новій вкладці (немає куди «назад») — у кабінет за роллю
  const closePage = () => {
    // idx — лічильник переходів React Router усередині платформи (history.length рахує й чужі сторінки вкладки)
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate(me?.userType === "student" ? "/student/homework" : me?.userType === "teacher" ? "/teacher/general" : "/");
  };

  if (error && !payload) return <styled.Message>{error}</styled.Message>;
  if (!payload) return <styled.Message>Завантажуємо ДЗ…</styled.Message>;
  if (!total) return <styled.Message>У цьому уроці немає ДЗ</styled.Message>;

  const slide = slides[index];
  const { marked, lesson } = payload;
  const score = marked.score;

  return (
    <AnswersContext.Provider value={store}>
      <styled.Page>
        {readOnly && (
          <hw.ReviewBar>
            ДЗ учня <b>{marked.studentName}</b> · {lesson.title}
            {marked.submittedAt ? (
              <span>
                здано {new Date(marked.submittedAt).toLocaleDateString("uk-UA")} · правильних {score?.correct ?? 0} з {score?.total ?? 0}
              </span>
            ) : (
              <span>ще не здано</span>
            )}
          </hw.ReviewBar>
        )}

        <styled.Viewport ref={viewportRef}>
          <styled.Stage style={{ width: STAGE_W, height: STAGE_H, transform: `scale(${scale})` }}>
            <hw.StageLock $locked={readOnly}>
              <SlideFrame key={slide.id} slide={slide} showAnswers={false} lesson={lesson} />
            </hw.StageLock>
          </styled.Stage>
        </styled.Viewport>

        <hw.Rotate>Поверни телефон горизонтально 📱↔️</hw.Rotate>

        <styled.Controls>
          <styled.Lesson>
            ДЗ · Lesson {lesson.number}. {lesson.title}
          </styled.Lesson>
          <styled.Nav>
            <styled.IconButton onClick={() => go((i) => i - 1)} disabled={index === 0} title="Previous (←)">
              <LuChevronLeft size={22} />
            </styled.IconButton>
            <hw.Dots>
              {slides.map((item, i) => {
                const r = results[item.id];
                // Вчитель: зелене — все правильно, жовте — частково, червоне — вправа не виконана
                const state = readOnly
                  ? !r || !r.answered ? "miss" : r.correct === r.total ? "ok" : "part"
                  : isDone(item) ? "done" : null;
                return <hw.Dot key={item.id} $current={i === index} $state={state} onClick={() => go(i)} title={`${i + 1}. ${item.title}`} />;
              })}
            </hw.Dots>
            <styled.IconButton onClick={() => go((i) => i + 1)} disabled={index === total - 1} title="Next (→)">
              <LuChevronRight size={22} />
            </styled.IconButton>
          </styled.Nav>
          <styled.Tools>
            {!readOnly && index === total - 1 && (
              <hw.Submit onClick={trySend} disabled={sending}>
                <LuSend size={18} /> {sending ? "Надсилаємо…" : marked.submittedAt ? "Надіслати ще раз" : "Надіслати ДЗ"}
              </hw.Submit>
            )}
            <styled.Tool
              onClick={() => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.())}
              title="Fullscreen"
            >
              <LuMaximize size={18} />
            </styled.Tool>
            <styled.Tool onClick={closePage} title="Close">
              <LuX size={18} />
            </styled.Tool>
          </styled.Tools>
        </styled.Controls>

        {confirm && (
          <hw.Overlay>
            <hw.Dialog>
              <h3>Ще не все виконано</h3>
              <p>Без відповідей: {confirm.map((n) => `вправа ${n}`).join(", ")}.</p>
              <hw.DialogButtons>
                <hw.Secondary onClick={() => { setConfirm(null); go(confirm[0] - 1); }}>Повернутися</hw.Secondary>
                <hw.Submit onClick={send}>Все одно надіслати</hw.Submit>
              </hw.DialogButtons>
            </hw.Dialog>
          </hw.Overlay>
        )}

        {done && (
          <hw.Overlay>
            <hw.Dialog>
              {done.starAwarded ? (
                <hw.Star>
                  +1 <StarIcon size={40} />
                  <span>Ти отримав(ла) зірку!</span>
                </hw.Star>
              ) : (
                <hw.Star>
                  <LuCheck size={40} />
                  <span>ДЗ оновлено</span>
                </hw.Star>
              )}
              <p>
                Правильних відповідей: <b>{done.score.correct}</b> з {done.score.total}. Вчитель побачить твоє ДЗ.
              </p>
              <hw.DialogButtons>
                <hw.Secondary onClick={() => setDone(null)}>Переглянути ще раз</hw.Secondary>
                <hw.Submit onClick={() => navigate("/student/homework")}>До домашніх завдань</hw.Submit>
              </hw.DialogButtons>
            </hw.Dialog>
          </hw.Overlay>
        )}

        {error && payload && <hw.Toast onClick={() => setError(null)}>{error}</hw.Toast>}
      </styled.Page>
    </AnswersContext.Provider>
  );
}

export default HomeworkPage;
